/** Compliance route: URL-backed work, report and framework views. */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import {
  findingsSortSchema,
  reportKindSchema,
} from "@/features/compliance/schemas";
import {
  fetchComplianceDashboard,
  fetchReports,
} from "@/features/compliance/queries";
import { queryKeys } from "@/lib/query-keys";
import { frameworkReviewScopeSchema, reviewRequestRefSchema } from "@/features/compliance/review-scope";
import { CompliancePage } from "@/features/compliance/pages/compliance-page";
import { workFilterSchema } from "@/features/compliance/work-queue";

const searchSchema = z.object({
  // Work is the default; reports and frameworks have explicit tab links.
  // Evidence and Exports are accepted only to be redirected: law 4 puts
  // signed evidence in Audit, and beforeLoad sends those values there.
  tab: z
    .enum(["work", "reports", "frameworks", "evidence", "exports"])
    .optional()
    .catch(undefined),
  // The reporting period. "Not run in this period" is a finding, and a
  // finding whose window lives in component state is one two people
  // cannot compare.
  period: z.enum(["current", "previous", "year"]).optional().catch(undefined),
  // The findings table's search and sort live here, not in component
  // state. An operator working a findings list opens a control, reads it,
  // comes back — and used to land on an unfiltered, default-sorted table,
  // having lost their place. Putting it in the URL also makes "the three
  // criticals I am looking at" a link someone can paste to a colleague,
  // which is most of what this table is for.
  //
  // .catch(undefined) rather than a hard parse: a stale or hand-edited
  // link should render the default table, never a route error.
  q: z.string().max(200).optional().catch(undefined),
  sort: findingsSortSchema.optional().catch(undefined),
  // The report card a control page linked to. A control's "carries the
  // record" link promises a specific card; landing at the top of a
  // fourteen-card catalogue breaks that promise, so the kind travels in
  // the URL and the tab scrolls to it.
  report: reportKindSchema.optional().catch(undefined),
  reviewFramework: frameworkReviewScopeSchema.optional().catch(undefined),
  reviewRequest: reviewRequestRefSchema.optional().catch(undefined),
  work: workFilterSchema.optional().catch(undefined),
});

// Phase C: prefetch the NEW catalog-backed dashboard (was the
// legacy /posture endpoint pre-cutover). Both prefetches run in
// parallel so switching tabs never shows a spinner.
export const Route = createFileRoute("/_authed/tenants/$slug/posture/compliance/")({
  validateSearch: searchSchema,
  // Law 4: Evidence and Exports live in Audit. A link that still names
  // them lands on the Audit view that carries them now.
  beforeLoad: ({ params, search }) => {
    if (search.tab === "evidence" || search.tab === "exports") {
      throw redirect({
        to: "/tenants/$slug/audit",
        params,
        search: { view: search.tab === "evidence" ? "evidence" : "signed-records" },
        replace: true,
      });
    }
  },
  loader: ({ context }) => {
    const tid = context.tenant.id;
    // Warm tab-local data without making Work depend on report availability.
    // Each tab renders its own query failure and retry action.
    void Promise.allSettled([
      context.queryClient.ensureQueryData({
        queryKey: ["compliance-v2", "dashboard", tid],
        queryFn: () => fetchComplianceDashboard(tid),
      }),
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.compliance.reports(tid),
        queryFn: () => fetchReports(tid),
      }),
    ]);
  },
  component: CompliancePage,
});
